import { Spinner } from "../../../common/spinner";
import { useRef, useState } from "react";
import { TProcessStates } from "../../../../lib/types/general";
import { useIndustryRoles } from "../../../../services/generalInfo";
import {
    AddEmployeeRoleModal,
    EmployeeRolesAdmin,
} from "./employee_roles_admin";
import { useEmployeeRoles } from "../../../../services/employee_roles";
import { Pagination } from "../../../common/pagination";
import { GENERAL_PAGINATION_SIZE } from "../../../../lib/common/constants";
import { TAddEmployeeRole } from "../../../../services/backoffice/backoffice-service";
import { TSkills } from "../../../../lib/types/skills";

export const PositionsAdmin = () => {
    const [addingSkillState, setAddingSkillState] =
        useState<TProcessStates>("idle");
    const { addIndustryRole, getSkillListNoLimit, updateIndustryRole } =
        useIndustryRoles();
    const employeeRolesListRef = useRef<HTMLDivElement>(null);
    const {
        employeeRoles,
        updateEmployeeRole,
        getRolesList,
        rolesCount,
        currentPage,
        setCurrentPage,
        resetEmployeeList,
        addNewEmployeeRole,
        workingState,
        allSkills,
        setAllSkills,
        getAllSkillsForEmployeeRoles,
        getSkillListWithSearchFilterInAddModal,
        skillsForModal,
    } = useEmployeeRoles();
    const [searchName, setSearchName] = useState<string>("");
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [openAddEmployeeRoleModal, setOpenAddEmployeeRoleModal] =
        useState<boolean>(false);
    const [editingRoleId, setEditingRoleId] = useState<number | undefined>(
        undefined
    );

    const handlePositionAdded = async (value: string) => {
        if (!value.trim()) return;
        setAddingSkillState("working");
        await addIndustryRole(value);
        await getSkillListNoLimit(0, { name: "" });
        setAddingSkillState("done");
    };

    const handleUpdateRole = async (
        role_id: number,
        updatedName?: string,
        updatedSkills?: number[],
        searchName?: string
    ) => {
        await updateEmployeeRole({
            employee_role_id: role_id,
            role_name: updatedName,
            skills_ids: updatedSkills,
            search_name: searchName,
        });
    };

    const searchRoleByNameHandler = async (nameToSearch: string) => {
        if (isSearching) return;

        setIsSearching(true);

        await getRolesList(0, {
            name: nameToSearch.trim(),
        });

        setIsSearching(false);
    };

    const applyFiltersRoleSearchHandler = () => {
        searchRoleByNameHandler(searchName);
    };

    const resetFiltersRoleHandler = () => {
        setSearchName("");
        resetEmployeeList();
    };

    const addNewEmployeeRoleHandler = async (props: TAddEmployeeRole) => {
        await addNewEmployeeRole(props);
        setOpenAddEmployeeRoleModal(false);
    };

    const editSkillNameHandle = async (
        posId: string,
        newName: string,
        editableSkillsCallback?: React.Dispatch<React.SetStateAction<TSkills[]>>
    ) => {
        await updateIndustryRole(posId, newName);
        const result = await getAllSkillsForEmployeeRoles(0, { name: "" });
        setAllSkills(result.data.skillsData);

        if (editableSkillsCallback) {
            editableSkillsCallback(result.data.skillsData);
        }
    };

    return (
        <div>
            <div>
                <div
                    className="admin-section p-3 mb-2"
                    ref={employeeRolesListRef}
                >
                    <div className="row">
                        <div className="col-lg-5">
                            <h4 className="text-secondary fw-bold ">
                                Employee Roles
                            </h4>
                        </div>
                        <div className="col ">
                            <div className="input-group mb-3">
                                <input
                                    type="text"
                                    className="form-control rounded-end-0 rounded-start-3"
                                    placeholder="Search Role"
                                    aria-label="Search Role"
                                    aria-describedby="button-Search-Role"
                                    value={searchName}
                                    onChange={(e) =>
                                        setSearchName(e.target.value)
                                    }
                                    disabled={editingRoleId !== undefined}
                                />
                                <button
                                    className="btn btn-light border rounded-start-0 rounded-end-3"
                                    type="button"
                                    id="button-Search-Role"
                                    onClick={applyFiltersRoleSearchHandler}
                                    disabled={editingRoleId !== undefined}
                                >
                                    <i className="fa-solid fa-magnifying-glass me-2"></i>
                                    Search
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-lg-7">
                            <p>
                                {" "}
                                <small>
                                    Manage all your employee roles here: search,
                                    add new ones, edit names, and assign skills.
                                </small>
                            </p>
                        </div>
                        <div className="col text-end">
                            <button
                                className="btn btn-light text-primary border-primary rounded-3 me-2 mt-lg-2"
                                onClick={resetFiltersRoleHandler}
                                disabled={editingRoleId !== undefined}
                            >
                                {" "}
                                <i className="fa-solid fa-arrows-rotate"></i>
                            </button>
                            <button
                                className="btn btn-light text-primary border-primary rounded-3 mt-lg-2"
                                onClick={() =>
                                    setOpenAddEmployeeRoleModal(true)
                                }
                                disabled={editingRoleId !== undefined}
                            >
                                <i className="fa-solid fa-circle-plus me-2"></i>
                                Add New Role
                            </button>
                        </div>
                    </div>
                    {workingState === "working" && (
                        <p className="text-primary">
                            <Spinner spinnerColor="text-primary" />
                        </p>
                    )}
                    {workingState === "done" && (
                        <>
                            <EmployeeRolesAdmin
                                employee_roles={employeeRoles}
                                positions={allSkills}
                                updateRoleHandler={handleUpdateRole}
                                searchName={searchName}
                                getAllSkillsForEmployeeRoles={
                                    getAllSkillsForEmployeeRoles
                                }
                                addIndustryRole={addIndustryRole}
                                updateSkillName={editSkillNameHandle}
                                editingRoleId={editingRoleId}
                                setEditingRoleId={setEditingRoleId}
                            />
                            <div className="mt-3">
                                <Pagination
                                    currentPage={currentPage}
                                    totalCount={rolesCount}
                                    itemsPerPage={GENERAL_PAGINATION_SIZE}
                                    refObject={employeeRolesListRef}
                                    onPageChange={(offset) => {
                                        getRolesList(offset, { name: "" });
                                        setCurrentPage(offset + 1);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            {openAddEmployeeRoleModal && (
                <AddEmployeeRoleModal
                    openAddEmployeeRoleModal={openAddEmployeeRoleModal}
                    onClose={() => {
                        setOpenAddEmployeeRoleModal(false);
                    }}
                    addNewEmployeeRoleHandler={addNewEmployeeRoleHandler}
                    allSkills={skillsForModal}
                    addNewSkillHandler={handlePositionAdded}
                    addingSkillState={addingSkillState}
                    getSkillListWithSearchFilterInAddModal={
                        getSkillListWithSearchFilterInAddModal
                    }
                    setAllSkills={setAllSkills}
                />
            )}
        </div>
    );
};
